import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  LegendProps,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { BarChartData } from './BarChart.controller';
import { ChartVerticalAxisTick } from '../LineChart/ChartVerticalAxisTick';
import { ChartHorizontalAxisTick } from '../LineChart/ChartHorizontalAxisTick';
import classes from './BarChart.module.scss';
import { BarChartTooltip } from './BarChartTooltip';
import { CHART_STROKE_COLOR } from '../chartConsts';
import classNames from 'classnames';
import { ConfigChartTicks, FillingDataConfig, TickChart } from '../helpers/chartHelpers';
import { useBreakpoints } from 'hooks/useBreakpoints';

type BarChartViewProps = {
  loading: boolean;
  activeMetrics: string[];
  chartData: BarChartData[];
  ticksConfig?: ConfigChartTicks;
  ticksChart: TickChart[];
  fillingDataConfig: FillingDataConfig;
  hideGridLinesEveryFourLines: boolean;
  hideGridLinesEverySevenLines: boolean;
};

const CustomLegend = ({ payload }: LegendProps) => {
  if (!payload) {
    return null;
  }
  return (
    <ul className={classes.CustomLegendContainer}>
      {payload.map(entry => (
        <li key={entry.value}>
          <div className={classes[`Icon${entry.value}`]}></div>
          <span>{entry.value}</span>
        </li>
      ))}
    </ul>
  );
};

export const BarChartView = ({
  activeMetrics,
  chartData,
  ticksChart,
  ticksConfig,
  hideGridLinesEverySevenLines,
  hideGridLinesEveryFourLines
}: BarChartViewProps) => {
  const breakpoint = useBreakpoints();
  const isTablet = breakpoint === 'tabletportrait';
  const isMonthly = ticksConfig?.displayMonthly;
  const isShowEveryWeekPoint = ticksConfig?.displayWeekly && ticksConfig.showLabelAfterEveryNTicks === 1;

  return (
    <ResponsiveContainer
      height={485}
      className={classNames(classes.ChartWrapper, {
        [classes.HideGridLinesEveryTwoLines]: ticksConfig?.showLabelAfterEveryNTicks === 2,
        [classes.NotHideLastLine]: ticksConfig?.showLabelAfterEveryNTicks === 2
      })}>
      <BarChart barCategoryGap={'10%'} margin={{ top: 32, left: isTablet ? -32 : 0 }} data={chartData}>
        <CartesianGrid
          stroke={CHART_STROKE_COLOR}
          verticalCoordinatesGenerator={
            hideGridLinesEveryFourLines || isMonthly || hideGridLinesEverySevenLines || isShowEveryWeekPoint
              ? props => {
                  const startPosition = props.offset?.left ?? 0;
                  const xAxisWidth = props.xAxis?.width ?? 0;

                  const tickWidth = xAxisWidth / ticksChart.length;
                  const marginBetweenBars = tickWidth * 0.1; // default margin between bars is 10% of its size
                  const barSize = tickWidth - marginBetweenBars * 2;

                  const chartGridPositions = ticksChart.reduce<number[]>((currentPositions, _, index) => {
                    let everyNBars = isMonthly ? 1 : 4;
                    if (hideGridLinesEverySevenLines) {
                      everyNBars = 7;
                    } else if (isShowEveryWeekPoint) {
                      everyNBars = 1;
                    }
                    if (index % everyNBars === 0 && index !== 0) {
                      return [...currentPositions, barSize * index + startPosition + marginBetweenBars * 2 * index];
                    } else {
                      return currentPositions;
                    }
                  }, []);
                  chartGridPositions.push(xAxisWidth + startPosition); // draw the last line of the grid
                  return chartGridPositions;
                }
              : undefined
          }
        />
        <XAxis
          interval={0}
          tickLine={false}
          dataKey={'fakeValue'}
          stroke={CHART_STROKE_COLOR}
          tick={props => (
            <ChartHorizontalAxisTick {...props} ticksConfig={ticksConfig} ticksChart={ticksChart} isBarChart />
          )}
        />
        <YAxis
          axisLine={true}
          tickLine={false}
          stroke={CHART_STROKE_COLOR}
          tick={props => <ChartVerticalAxisTick shouldNormalize={false} {...props} allowDecimals={false} />}
        />
        <Legend content={<CustomLegend />} iconType={'circle'} wrapperStyle={{ position: 'relative' }} />
        <Tooltip
          filterNull={false}
          cursor={false}
          content={props => (
            <BarChartTooltip
              {...props}
              payload={props.payload}
              isWeekly={ticksConfig?.displayWeekly}
              displayMonthly={ticksConfig?.displayMonthly}
              activeMetrics={activeMetrics}
              chartData={chartData}
            />
          )}
        />
        {activeMetrics.map(metric => (
          <Bar
            className={classes[`BarColors${metric}`]}
            key={metric}
            maxBarSize={22}
            dataKey={metric.toLowerCase()}
            stackId={'metrics'}></Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
